import { useHeyApi, type CentroPortalClient } from "@platform/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { isJSON } from "@platform/fe-utils";

import {
  buildPopupCustomTextCellKey,
  parsePopupCustomTextCellKey,
  POPUP_CUSTOM_TEXT_OVERRIDE_TYPE,
  JsonValue,
  PopupCustomTextListResponse,
  PopupCustomTextRecord,
  PopupCustomTextScope,
  PopupCustomTextUpsertBody,
  PopupCustomTextUpsertRequest,
} from "../utils/popupCustomText";

const FIELD_OVERRIDES_QUERY_TAG = "quotes-field-overrides";

// Keeps each list request's URL well under the ingress request-line limit
// (nginx's default large-client-header-buffers is 8KB) regardless of how
// many plans an RFP has.
const FIELD_OVERRIDES_SCOPE_ID_BATCH_SIZE = 50;

const fieldOverridesQueryKey = (
  rfpId: string | undefined,
  proposalPlanIds: readonly string[],
): [string, string | undefined, readonly string[]] => [
  FIELD_OVERRIDES_QUERY_TAG,
  rfpId,
  proposalPlanIds,
];

const chunk = <T>(values: readonly T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let start = 0; start < values.length; start += size) {
    chunks.push(values.slice(start, start + size));
  }

  return chunks;
};

const fieldOverridesPath = (rfpId: string): string =>
  `/rfps/${encodeURIComponent(rfpId)}/field-overrides`;

const fieldOverridesListPath = (
  rfpId: string,
  proposalPlanIds: readonly string[],
): string => {
  const searchParams = new URLSearchParams({ scopeType: "proposal_plan" });
  proposalPlanIds.forEach((proposalPlanId) =>
    searchParams.append("scopeId", proposalPlanId),
  );

  return `${fieldOverridesPath(rfpId)}?${searchParams.toString()}`;
};

const proposalPlanScope = (
  rfpId: string,
  proposalPlanId: string,
): PopupCustomTextScope => ({
  type: "proposalPlan",
  rfpId,
  proposalPlanId,
});

const uniqueSortedValues = (values: readonly string[]): string[] =>
  [...new Set(values)].sort();

const toCustomTextByCellKey = (
  records: PopupCustomTextRecord[],
): ReadonlyMap<string, string> =>
  new Map(
    records
      .filter(
        (record): record is PopupCustomTextRecord & { customText: string } =>
          record.customText !== null,
      )
      .map((record) => [
        buildPopupCustomTextCellKey(record),
        record.customText,
      ]),
  );

const normalizeOriginalValue = (
  originalValue: JsonValue | undefined,
): JsonValue | null => {
  if (
    originalValue == null ||
    originalValue === undefined ||
    originalValue === ""
  ) {
    return null;
  }
  if (typeof originalValue === "object" && !Array.isArray(originalValue)) {
    // The backend expects an object value to be sent as a JSON string, but it
    // returns it parsed. To avoid confusion, we re-stringify it before sending
    // it back if it's already an object.
    return originalValue;
  }

  if (typeof originalValue === "string" && isJSON(originalValue)) {
    // Similar to the above, if we receive a string that is JSON parseable, we
    // parse it so that the editor receives it in the expected format (e.g. an
    // object instead of a JSON string). However, when sending it back to the
    // backend, we need to ensure it's a JSON string.
    const parsed = JSON.parse(originalValue) as JsonValue;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
    return { value: parsed } as JsonValue;
  }
  return { value: originalValue } as JsonValue;
};

interface UseQuotesFieldOverridesOptions {
  rfpId: string | undefined;
  coverageType: string | undefined;
  proposalPlanIds: readonly string[];
  enabled?: boolean;
  /** Called when a custom-text save fails (after optimistic rollback). */
  onSaveError?: (error: Error) => void;
}

interface UseQuotesFieldOverridesResult {
  customTextByCellKey: ReadonlyMap<string, string>;
  onCustomTextChange: (
    cellKey: string,
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
  isLoading: boolean;
  error: Error | null;
}

// Field overrides ("Enter Custom Text" values) are stored keyed to the source
// plan's proposalPlanId — separately from plan.benefits — so Paste Plan has to
// replay them onto the newly created plan the same way it replays rate
// schedules onto data.id.
export const copyProposalPlanFieldOverrides = async ({
  centroPortalClient,
  rfpId,
  sourceProposalPlanId,
  targetProposalPlanId,
}: {
  centroPortalClient: CentroPortalClient;
  rfpId: string;
  sourceProposalPlanId: string;
  targetProposalPlanId: string;
}): Promise<void> => {
  const { data } = await centroPortalClient.get<
    PopupCustomTextListResponse,
    unknown,
    true
  >({
    url: fieldOverridesListPath(rfpId, [sourceProposalPlanId]),
    responseType: "json",
    throwOnError: true,
  });

  if (data.overrides.length === 0) return;

  const overrides: PopupCustomTextUpsertRequest[] = data.overrides.map(
    (record) => ({
      scope: proposalPlanScope(rfpId, targetProposalPlanId),
      coverageType: record.coverageType,
      benefitPlanId: targetProposalPlanId,
      benefitKey: record.benefitKey,
      propertyKey: record.propertyKey,
      network: record.network,
      overrideType: record.overrideType,
      customText: record.customText,
      originalValue: record.originalValue,
    }),
  );

  await centroPortalClient.put<PopupCustomTextListResponse, unknown, true>({
    url: fieldOverridesPath(rfpId),
    body: { overrides } satisfies PopupCustomTextUpsertBody,
    responseType: "json",
    throwOnError: true,
  });
};

export const useQuotesFieldOverrides = ({
  rfpId,
  coverageType,
  proposalPlanIds,
  enabled = true,
  onSaveError,
}: UseQuotesFieldOverridesOptions): UseQuotesFieldOverridesResult => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();
  const scopedProposalPlanIds = useMemo(
    () => uniqueSortedValues(proposalPlanIds),
    [proposalPlanIds],
  );
  const queryKey = fieldOverridesQueryKey(rfpId, scopedProposalPlanIds);

  const { data, isLoading, error } = useQuery({
    queryKey,
    enabled: enabled && !!rfpId && scopedProposalPlanIds.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!rfpId || scopedProposalPlanIds.length === 0) {
        return {
          rfpId: "",
          overrides: [],
        } satisfies PopupCustomTextListResponse;
      }

      const responses = await Promise.all(
        chunk(scopedProposalPlanIds, FIELD_OVERRIDES_SCOPE_ID_BATCH_SIZE).map(
          (proposalPlanIdBatch) =>
            centroPortalClient.get<PopupCustomTextListResponse, unknown, true>({
              url: fieldOverridesListPath(rfpId, proposalPlanIdBatch),
              responseType: "json",
              throwOnError: true,
            }),
        ),
      );

      return {
        rfpId,
        overrides: responses.flatMap((response) => response.data.overrides),
      } satisfies PopupCustomTextListResponse;
    },
  });

  const customTextByCellKey = useMemo(
    () => toCustomTextByCellKey(data?.overrides ?? []),
    [data?.overrides],
  );

  const mutation = useMutation({
    mutationFn: async (override: PopupCustomTextUpsertRequest) => {
      if (!rfpId) return;

      const body: PopupCustomTextUpsertBody = { overrides: [override] };
      await centroPortalClient.put<PopupCustomTextListResponse, unknown, true>({
        url: fieldOverridesPath(rfpId),
        body,
        responseType: "json",
        throwOnError: true,
      });
    },
    onMutate: async (override) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<PopupCustomTextListResponse>(queryKey);

      queryClient.setQueryData<PopupCustomTextListResponse>(
        queryKey,
        (current) => {
          const currentResponse = current ?? {
            rfpId: rfpId ?? "",
            overrides: [],
          };
          const overrideCellKey = buildPopupCustomTextCellKey(override);
          const nextOverrides = currentResponse.overrides.filter(
            (record) => buildPopupCustomTextCellKey(record) !== overrideCellKey,
          );

          if (override.customText !== null) {
            nextOverrides.push({
              ...override,
              updatedAt: new Date().toISOString(),
            });
          }

          return { ...currentResponse, overrides: nextOverrides };
        },
      );

      return { previous };
    },
    onError: (_err, _override, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      if (_err instanceof Error) {
        onSaveError?.(_err);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const onCustomTextChange = useCallback(
    (cellKey: string, customText: string | null, originalValue?: JsonValue) => {
      if (!rfpId || !coverageType) return;

      const identity = parsePopupCustomTextCellKey(cellKey);
      if (!identity) return;

      const originalValueParse = normalizeOriginalValue(originalValue);

      mutation.mutate({
        scope: proposalPlanScope(rfpId, identity.benefitPlanId),
        coverageType,
        ...identity,
        customText,
        originalValue: originalValueParse,
        overrideType: POPUP_CUSTOM_TEXT_OVERRIDE_TYPE,
      });
    },
    [coverageType, mutation, rfpId],
  );

  return {
    customTextByCellKey,
    onCustomTextChange,
    isLoading,
    error: error instanceof Error ? error : null,
  };
};
